import { FormikErrors, FormikTouched, useFormik } from 'formik';
import RootLayout from '../../layouts/RootLayout';
import FormInput from '../../../components/forms/FormInput';
import { Button } from '../../../components/buttons/Button';
import { useAuth } from '../../../lib/auth';
import { useSearchParams } from 'react-router';
import { ChangeEvent, SetStateAction, useState } from 'react';
import Alert from '../../../components/Alert.tsx';
import API, { API_URL } from '../../../lib/api.ts';
import {
    cn,
    PUBLIC_EMAIL_DOMAINS,
    transformLaravelToFormikErrors,
} from '../../../lib/helpers.ts';
import { SchemaDrivenFormFieldProps } from '../../../lib/types.ts';
import HeadLogo from '../../../components/logos/HeadLogo.tsx';
import * as Yup from 'yup';

// TODO: refactor
export function renderSchemaForm(
    values: object,
    errors: FormikErrors<object>,
    touched: FormikTouched<object>,
    setFieldValue: (
        field: string,
        value: never,
        shouldValidate?: boolean
    ) => Promise<FormikErrors<object>> | Promise<void>,
    setValues: (
        values: SetStateAction<object>,
        shouldValidate?: boolean
    ) => Promise<FormikErrors<object>> | Promise<void>,
    handleChange: {
        (e: ChangeEvent<never>): void;
        <T_1 = string | ChangeEvent<never>>(
            field: T_1
        ): T_1 extends ChangeEvent<never>
            ? void
            : (e: string | ChangeEvent<never>) => void;
    }
) {
    const renderForm = ({
        type,
        name,
        label,
        content,
        required,
        placeholder,
        readonly,
        disabled,
        className,
    }: SchemaDrivenFormFieldProps) => {
        const requiredWithDefault = required ?? true;

        switch (type) {
            case 'heading':
                return (
                    <h2
                        key={name}
                        className={cn(
                            '!mt-12 mb-0 block text-2xl font-bold',
                            className
                        )}
                    >
                        {label}
                    </h2>
                );
            case 'subheading':
                return (
                    <p key={name} className="!mt-0 block text-sm text-ink-800">
                        {label}
                    </p>
                );
            case 'hidden':
                return null;
            case 'grid':
                return (
                    <div
                        key={name}
                        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
                    >
                        {content?.map(renderForm)}
                    </div>
                );
            case 'html':
                return (
                    <div key={name} className="w-full">
                        {label}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="flex items-baseline" key={name}>
                        <input
                            type="checkbox"
                            name={name}
                            id={name}
                            className="mr-3 h-4 w-4 translate-y-0.5 rounded border border-cloud-300"
                            required={requiredWithDefault}
                            onChange={handleChange}
                        />
                        <label htmlFor={name}>
                            {label}
                            {requiredWithDefault && (
                                <span className="text-red-500">&nbsp;*</span>
                            )}
                        </label>
                    </div>
                );
            default:
                return (
                    <FormInput
                        key={name}
                        name={name}
                        label={label!}
                        required={requiredWithDefault}
                        onChange={handleChange}
                        value={values[name as keyof typeof values]}
                        type={type}
                        error={
                            errors[name as keyof typeof errors] &&
                            touched[name as keyof typeof touched]
                                ? errors[name as keyof typeof errors]
                                : null
                        }
                        placeholder={placeholder}
                        readonly={readonly}
                        disabled={disabled}
                    />
                );
        }
    };

    return renderForm;
}

export default function AcceptInvitationPage() {
    useAuth({
        middleware: 'guest',
        redirectTo: '/dashboard',
        tmp: 'SignUpPage',
    });

    // const navigate = useNavigate();

    const [passwordResetStatus, setPasswordResetStatus] = useState<
        undefined | string
    >(undefined);

    const [searchParams] = useSearchParams();

    const validationSchema = Yup.object().shape({
        first_name: Yup.string().required('Jméno je povinné pole.'),
        last_name: Yup.string().required('Příjmení je povinné pole.'),
        email: Yup.string()
            .email('E-mail není ve správném formátu.')
            .test(
                'email-domain',
                'Zdá se, že jste nezadali svůj pracovní e-mail. Potřebujeme vaši registraci ověřit, prosím klikněte na "jiná instituce" a vyplňte své údaje ručně.',
                (value) => {
                    // This applies only for school registration (not for other) - check values.institution_is_a_school
                    // if (!values.institution_is_a_school) return true;

                    const domain = value?.split('@')[1]?.toLowerCase();
                    if (!domain) return false;

                    return !PUBLIC_EMAIL_DOMAINS.includes(domain);
                }
            )
            .required('E-mail je povinné pole.'),
        phone: Yup.string().required('Telefonní číslo je povinné pole.'),
        password: Yup.string()
            .min(8, 'Heslo musí mít alespoň 8 znaků.')
            .required('Heslo je povinné pole.'),
        password_confirmation: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Hesla se neshodují.')
            .required('Heslo je povinné pole.'),
        tos_gdpr_confirmation: Yup.boolean().oneOf(
            [true],
            'Musíte souhlasit s podmínkami užití a zásadami ochrany osobních údajů.'
        ),
        newsletter_confirmation: Yup.boolean(),
        invitation_code: Yup.string(),
    });

    const {
        handleSubmit,
        handleChange,
        values,
        setSubmitting,
        isSubmitting,
        errors,
        touched,
        setErrors,
        setFieldValue,
        setValues,
    } = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: searchParams.get('email') ?? '',
            phone: '',
            password: '',
            password_confirmation: '',
            tos_gdpr_confirmation: false,
            newsletter_confirmation: false,
            invitation_code: searchParams.get('code') ?? '',
        },
        validationSchema,
        onSubmit: (values) => {
            // Take only numbers from phone
            setValues(
                {
                    ...values,
                    phone: values.phone.replace(/\D/g, ''),
                },
                false
            );

            API.post('/accept-invite', {
                ...values,
            })
                .then((res) => {
                    // @ts-ignore
                    window.dataLayer?.push({
                        event: 'registration',
                    });

                    setPasswordResetStatus(
                        res?.data?.status ?? 'Registrace byla úspěšná.'
                    );
                    // setTimeout(() => {
                    //     navigate('/dashboard');
                    // }, 5000);
                })
                .catch((err) => {
                    const errors = err?.response?.data?.errors;
                    if (errors)
                        setErrors(transformLaravelToFormikErrors(errors));
                })
                .finally(() => setSubmitting(false));
        },
    });

    const schema: SchemaDrivenFormFieldProps[] = [
        // ---------------------------------------------------------------------
        // {
        //     name: 'section-contact-person',
        //     type: 'heading',
        //     label: 'Kontaktní osoba',
        // },
        {
            name: 'name-surname',
            type: 'grid',
            content: [
                {
                    name: 'first_name',
                    type: 'text',
                    label: 'Jméno',
                },
                {
                    name: 'last_name',
                    type: 'text',
                    label: 'Příjmení',
                },
            ],
        },
        {
            name: 'email-phone',
            type: 'grid',
            content: [
                {
                    name: 'email',
                    type: 'email',
                    label: 'Pracovní e-mail',
                    placeholder: 'email@skola.cz',
                    readonly: true,
                },
                {
                    name: 'phone',
                    type: 'tel',
                    label: 'Telefonní číslo',
                    placeholder: '+420 123 456 789',
                },
            ],
        },
        {
            name: 'password-password-again',
            type: 'grid',
            content: [
                {
                    name: 'password',
                    type: 'password',
                    label: 'Heslo',
                },
                {
                    name: 'password_confirmation',
                    type: 'password',
                    label: 'Heslo znovu',
                },
            ],
        },
        {
            name: 'section-tos',
            type: 'heading',
            label: 'Podmínky užití',
        },
        {
            name: 'tos_gdpr_confirmation',
            type: 'checkbox',
            label: (
                <>
                    Souhlasím s{' '}
                    <a
                        href={`${API_URL}/../files/vsimavec-obchodni-podminky.pdf`}
                        target="_blank"
                        className="text-blue-500 underline"
                    >
                        podmínkami užití
                    </a>{' '}
                    a{' '}
                    <a
                        href={`${API_URL}/../files/vsimavec-zasady-ochrany-osobnich-udaju.pdf`}
                        target="_blank"
                        className="text-blue-500 underline"
                    >
                        zásadami ochrany osobních údajů
                    </a>
                    .
                </>
            ),
        },
        {
            name: 'newsletter_confirmation',
            type: 'checkbox',
            label: (
                <>
                    Chci dostávat informace o preventivních programech a
                    wellbeingu do škol.
                </>
            ),
            required: false,
        },
        { name: 'break', type: 'html', label: <br /> },
    ];

    const touchedAndError =
        Object.keys(errors).length > 0 && Object.keys(touched).length > 0;

    console.log(errors);

    return (
        <RootLayout>
            <main className="container mx-auto flex h-screen flex-col items-center justify-start px-4 py-32">
                <HeadLogo />
                <h1>Přijetí pozvánky</h1>
                <p className={cn('mb-6 text-ink-800', 'text-center')}>
                    Váš kolega vám poslal pozvánku do Všímavce. Pro dokončení účtu prosím vyplňte formulář.
                </p>
                {passwordResetStatus !== undefined ? (
                    <Alert type="success">{passwordResetStatus}</Alert>
                ) : (
                    <div className="w-full max-w-5xl">
                        <form
                            onSubmit={handleSubmit}
                            className="mx-auto flex w-full max-w-2xl flex-col space-y-4 pb-32"
                        >
                            {schema.map(
                                renderSchemaForm(
                                    values,
                                    errors,
                                    touched,
                                    setFieldValue,
                                    setValues,
                                    handleChange
                                )
                            )}

                            {touchedAndError ? (
                                <Alert type="error">
                                    Formulář obsahuje chyby. Prosím,
                                    zkontrolujte a opravte formulář.
                                </Alert>
                            ) : null}

                            <Button
                                type="submit"
                                color="product"
                                isLoading={isSubmitting}
                                disabled={touchedAndError}
                                className="text-base"
                            >
                                Dokončit registraci
                            </Button>
                        </form>
                    </div>
                )}
            </main>
        </RootLayout>
    );
}
